import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

interface PricingTier {
  id: string;
  tier_name: string;
  description: string | null;
  price_monthly: number | null;
  price_yearly: number | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
  features: any;
  max_jobs_per_month: number | null;
  created_at: string;
}

export function StripeSubscriptionManager() {
  const { user, orgId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);

  useEffect(() => {
    if (orgId) {
      loadData();
    }
  }, [orgId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load pricing tiers
      const { data: tiersData, error: tiersError } = await supabase
        .from("pricing_tiers")
        .select("*")
        .order("price_monthly", { ascending: true });

      if (tiersError) throw tiersError;
      setTiers(tiersData || []);

      // Load current subscription
      const { data: org, error: orgError } = await supabase
        .from("organizations")
        .select("pricing_tier_id, subscription_status, subscription_period, subscription_current_period_end")
        .eq("id", orgId)
        .single();

      if (orgError) throw orgError;
      setCurrentSubscription(org);
    } catch (error: any) {
      console.error("Error loading subscription data:", error);
      toast.error("Failed to load subscription information");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId: string, period: "monthly" | "yearly") => {
    try {
      setCheckoutLoading(tierId);

      const priceId = period === "monthly"
        ? tiers.find(t => t.id === tierId)?.stripe_price_id_monthly
        : tiers.find(t => t.id === tierId)?.stripe_price_id_yearly;

      if (!priceId) {
        toast.error("Pricing configuration not found");
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-stripe-checkout", {
        body: { priceId, period, orgId },
      });

      if (error) throw error;

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout process");
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {currentSubscription?.subscription_status && (
        <Card>
          <CardHeader>
            <CardTitle>Current Subscription</CardTitle>
            <CardDescription>
              Your subscription is{" "}
              <Badge variant={currentSubscription.subscription_status === "active" ? "default" : "destructive"}>
                {currentSubscription.subscription_status}
              </Badge>
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {tiers.map((tier) => {
          const isCurrent = currentSubscription?.pricing_tier_id === tier.id;

          return (
            <Card key={tier.id} className={isCurrent ? "border-primary" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{tier.tier_name}</CardTitle>
                  {isCurrent && <Badge>Current</Badge>}
                </div>
                <CardDescription>{tier.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">${tier.price_monthly || 0}</div>
                  <div className="text-sm text-muted-foreground">per month</div>
                </div>

                <div className="space-y-2">
                  {Array.isArray(tier.features) && tier.features.map((feature: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {!isCurrent && (
                  <Button
                    className="w-full"
                    onClick={() => handleSubscribe(tier.id, "monthly")}
                    disabled={checkoutLoading === tier.id}
                  >
                    {checkoutLoading === tier.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
